import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import {
  AdminNotificationCounts,
  ADMIN_NOTIFICATION_TYPES,
  AdminNotificationType,
} from "@/lib/services/admin-notification.service";
import { showAdminNotificationToast } from "@/components/admin/AdminNotificationToast";
import { showAdminBrowserNotification } from "@/lib/services/browser-notification.service";
import type { RealtimeChannel } from "@supabase/supabase-js";

interface AdminNotificationBadgeContextType {
  counts: AdminNotificationCounts;
  loading: boolean;
  isAdmin: boolean;
  refresh: () => Promise<void>;
}

const AdminNotificationBadgeContext = createContext<AdminNotificationBadgeContextType | undefined>(
  undefined
);

/**
 * Provider for admin notification badge counts
 * KISS: Simple context that manages admin notification counts
 * SOLID: SRP - focused on admin notification count management only
 */
export const AdminNotificationBadgeProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, getUserRole } = useAuth();
  const [counts, setCounts] = useState<AdminNotificationCounts>({
    total: 0,
    vehicles: 0,
    documents: 0,
    cancellations: 0,
  });
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);

  /**
   * Fetch unread counts from database
   */
  const refresh = useCallback(async () => {
    if (!user) {
      setCounts({ total: 0, vehicles: 0, documents: 0, cancellations: 0 });
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    try {
      // Check if user is admin
      const role = await getUserRole();
      if (role !== "admin") {
        setCounts({ total: 0, vehicles: 0, documents: 0, cancellations: 0 });
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      setIsAdmin(true);

      // Fetch unread counts
      const { data, error } = await supabase
        .from("notifications")
        .select("type")
        .eq("user_id", user.id)
        .eq("is_read", false)
        .in("type", ADMIN_NOTIFICATION_TYPES);

      if (error) {
        console.error("[AdminNotificationBadge] Error fetching counts:", error);
        setLoading(false);
        return;
      }

      const newCounts: AdminNotificationCounts = {
        total: 0,
        vehicles: 0,
        documents: 0,
        cancellations: 0,
      };

      (data || []).forEach((n) => {
        newCounts.total++;
        switch (n.type) {
          case "admin_vehicle_pending":
            newCounts.vehicles++;
            break;
          case "admin_document_pending":
            newCounts.documents++;
            break;
          case "admin_cancellation_pending":
            newCounts.cancellations++;
            break;
        }
      });

      setCounts(newCounts);
    } catch (error) {
      console.error("[AdminNotificationBadge] Exception:", error);
    } finally {
      setLoading(false);
    }
  }, [user, getUserRole]);

  /**
   * Set up realtime subscription
   */
  useEffect(() => {
    if (!user) {
      setCounts({ total: 0, vehicles: 0, documents: 0, cancellations: 0 });
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    // Initial fetch
    refresh();

    // Set up realtime subscription for admin notifications
    const channel = supabase
      .channel(`admin-notification-badge-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newRecord = payload.new as Record<string, unknown> | null;
          const type = newRecord?.type as AdminNotificationType;

          if (type && ADMIN_NOTIFICATION_TYPES.includes(type)) {
            // Refresh counts on any change
            refresh();

            // Handle INSERT events for notifications
            if (payload.eventType === "INSERT") {
              const message = (newRecord?.message as string) || "Nouvelle notification admin";

              // Show Toast
              showAdminNotificationToast(type, message, {
                duration: 8000,
              });

              // Show Browser Notification
              const categoryMap: Partial<Record<AdminNotificationType, "vehicle" | "document" | "cancellation" | "user">> = {
                admin_vehicle_pending: "vehicle",
                admin_document_pending: "document",
                admin_cancellation_pending: "cancellation",
                admin_renter_signup: "user",
              };

              const category = categoryMap[type];
              if (category) showAdminBrowserNotification(category, message);
            }
          }
        })
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          console.log("[AdminNotificationBadge] Realtime connected");
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user, refresh]);

  return (
    <AdminNotificationBadgeContext.Provider value={{ counts, loading, isAdmin, refresh }}>
      {children}
    </AdminNotificationBadgeContext.Provider>
  );
};

/**
 * Hook to access admin notification badge context
 */
export const useAdminNotificationBadge = () => {
  const context = useContext(AdminNotificationBadgeContext);
  if (context === undefined) {
    throw new Error("useAdminNotificationBadge must be used within AdminNotificationBadgeProvider");
  }
  return context;
};

